import { useState, useRef,useCallback, useEffect } from "react";
import { useCreateQuickServiceRequest } from "../../../hooks/useCraneQueries.js";
import LocationPickerModal, {reverseGeocode} from "./LocationPickerModal.jsx";
import {parseLatLng} from "./geo.js";

const OLA_API_KEY = process.env.REACT_APP_OLA_API_KEY;        
const DEBOUNCE_MS = 320; 

const SERVICE_LABEL = {
  crane:    "Crane",
  recovery: "Recovery van",
};

const FIXED_SLOTS = [
  { key: "front_left",  label: "Front left"  },
  { key: "front_right", label: "Front right" },
  { key: "rear_left",   label: "Rear left"   },
  { key: "rear_right",  label: "Rear right"  },
];

const EMPTY_QS_FORM = {
  vehicle_number:    "",
  pickup_location:   "",
  drop_location:     "",
  issue_description: "",
  owner_name:        "",
  owner_contact:     "",
  driver_name:       "",
  driver_contact:    "",
  urgent:            false,
};

// ── Validation helpers ────────────────────────────────────────────────────────
const PHONE_RE = /^[6-9]\d{9}$/;   // Indian mobile: starts 6–9, exactly 10 digits

function validateForm(form) {
  const errors = {};
  if (!form.vehicle_number.trim())  errors.vehicle_number  = "Vehicle plate number is required.";
  if (!form.pickup_location.trim()) errors.pickup_location = "Pickup location is required.";
  if (!form.drop_location.trim())   errors.drop_location   = "Drop / destination is required.";
  if (!form.owner_name.trim()) errors.owner_name = "Owner name is required.";
  if (!form.owner_contact.trim())
  errors.owner_contact = "Owner contact is required.";
  else if (!PHONE_RE.test(form.owner_contact.trim()))
  errors.owner_contact = "Enter a valid 10-digit mobile number (starts with 6–9).";
  if (form.driver_contact.trim() && !PHONE_RE.test(form.driver_contact.trim()))
    errors.driver_contact = "Enter a valid 10-digit mobile number (starts with 6–9).";
  return errors;
}

// / ── Autocomplete fetch ────────────────────────────────────────────────────────
async function fetchSuggestions(query) {
  if (!query.trim() || query.length < 3) return [];
  try {
    const res = await fetch(
      `https://api.olamaps.io/places/v1/autocomplete?input=${encodeURIComponent(query)}&api_key=${OLA_API_KEY}`
    );
    const data = await res.json();
    return data?.predictions ?? [];
  } catch {
    return [];
  }
}

async function fetchPlaceDetails(placeId) {
  try {
    const res = await fetch(
      `https://api.olamaps.io/places/v1/details?place_id=${placeId}&api_key=${OLA_API_KEY}`
    );
    const data = await res.json();
    const loc = data?.result?.geometry?.location;
    return loc ? { lat: loc.lat, lng: loc.lng } : null;
  } catch {
    return null;
  }
}

export default function CreatecraneServiceModal({ serviceType, onClose, onCreated, prefill = null,accidentCaseNo = null, }) {
  const mutation = useCreateQuickServiceRequest();

  const [form, setForm] = useState({
    ...EMPTY_QS_FORM,
    ...(prefill || {}),
    vehicle_number: (prefill?.vehicle_number || "").toUpperCase().replace(/[^A-Z0-9]/g, ""),
  });

  // ★ Coordinates — stored separately, appended to FormData on submit
  const [pickupCoords, setPickupCoords] = useState(null);  // { lat, lng } | null
  const [dropCoords,   setDropCoords]   = useState(null);  // { lat, lng } | null

  // ★ Which map picker is open: null | "pickup" | "drop"
  const [mapPickerField, setMapPickerField] = useState(null);

  // ★ Autocomplete state for each field
  const [pickupSugg,     setPickupSugg]     = useState([]);
  const [dropSugg,       setDropSugg]       = useState([]);
  const [showPickupSugg, setShowPickupSugg] = useState(false);
  const [showDropSugg,   setShowDropSugg]   = useState(false);
  const pickupDebounce = useRef(null);
  const dropDebounce   = useRef(null);

  // Errors only shown after first submit attempt
  const [errors,       setErrors]       = useState({});
  const [submitted,    setSubmitted]    = useState(false);

  // ── Photo state ───────────────────────────────────────────────────────────
  const [fixedPhotos,       setFixedPhotos]       = useState({ front_left: null, front_right: null, rear_left: null, rear_right: null });
  const [fixedPreviews,     setFixedPreviews]     = useState({ front_left: null, front_right: null, rear_left: null, rear_right: null });
  const [additionalPhotos,  setAdditionalPhotos]  = useState([]);
  const [additionalPreviews,setAdditionalPreviews] = useState([]);

  const [vehicleRcData, setVehicleRcData] = useState(null); // ← ULIP response stored here
  const [vehicleLookupLoading, setVehicleLookupLoading] = useState(false);
  const [vehicleError, setVehicleError] = useState(null);

  const userName = localStorage.getItem("name") || "ADMIN";

  const additionalInputRef = useRef(null);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const set = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const updated = { ...form, [field]: value };
    setForm(updated);
    // Re-run validation live once the user has attempted submit
    if (submitted) setErrors(validateForm(updated));
  };

  // ★ ── Location field typed input → autocomplete ──────────────────────────
  const handleLocationInput = (field) => (e) => {
    const val = e.target.value;
    const updated = { ...form, [field]: val };
    setForm(updated);
    if (submitted) setErrors(validateForm(updated));
    
    const parsed = parseLatLng(val);
    if (parsed) {
      if (field === "pickup_location") {
        setPickupCoords({ ...parsed, fromMap: true });
        clearTimeout(pickupDebounce.current);
        setPickupSugg([]); setShowPickupSugg(false);
      } else {
        setDropCoords({ ...parsed, fromMap: true });
        clearTimeout(dropDebounce.current);
        setDropSugg([]); setShowDropSugg(false);
      }
      return;
    }

    // Reset coords if user is typing manually
    if (field === "pickup_location") {
      setPickupCoords(null);
      clearTimeout(pickupDebounce.current);
      if (val.length < 3) { setPickupSugg([]); setShowPickupSugg(false); return; }
      pickupDebounce.current = setTimeout(async () => {
        const results = await fetchSuggestions(val);
        setPickupSugg(results);
        setShowPickupSugg(results.length > 0);
      }, DEBOUNCE_MS);
    } else {
      setDropCoords(null);
      clearTimeout(dropDebounce.current);
      if (val.length < 3) { setDropSugg([]); setShowDropSugg(false); return; }
      dropDebounce.current = setTimeout(async () => {
        const results = await fetchSuggestions(val);
        setDropSugg(results);
        setShowDropSugg(results.length > 0);
      }, DEBOUNCE_MS);
    }
  };

  const handleLocationBlur = (field) => async () => {
    setTimeout(() => (
      field === "pickup_location" ? setShowPickupSugg(false) : setShowDropSugg(false)
    ), 150);
 
    const parsed = parseLatLng(form[field]);
    if (!parsed) return;
    const addr = await reverseGeocode(parsed.lat, parsed.lng);
    if (addr && addr !== "Unknown address") {
      setForm((f) => ({ ...f, [field]: addr }));
    }
  };

  // ★ Suggestion selected from dropdown
  const handleSuggestionSelect = async (field, suggestion) => {
    const label = suggestion.description ??
                  suggestion.structured_formatting?.main_text ?? "";
    const updated = { ...form, [field]: label };
    setForm(updated);
    if (submitted) setErrors(validateForm(updated));

    if (field === "pickup_location") {
      setShowPickupSugg(false); setPickupSugg([]);
    } else {
      setShowDropSugg(false); setDropSugg([]);
    }

    if (suggestion.place_id) {
      const loc = await fetchPlaceDetails(suggestion.place_id);
      if (loc) {
       if (field === "pickup_location") setPickupCoords({ ...loc, fromMap: false }); // ★
       else setDropCoords({ ...loc, fromMap: false });                          
      }
    }
  };

  // ★ Map picker confirmed
  const handleMapConfirm = useCallback((field, { address, coords }) => {
    const updated = { ...form, [field]: address };
    setForm(updated);
    if (submitted) setErrors(validateForm(updated));
    if (field === "pickup_location" && coords) setPickupCoords({ ...coords, fromMap: true }); // ★
    if (field === "drop_location"   && coords) setDropCoords({ ...coords, fromMap: true });   // ★
      setMapPickerField(null);
  }, [form, submitted]);

  const handleFixedPhoto = (slotKey, file) => {
    if (!file) return;
    setFixedPhotos(prev  => ({ ...prev, [slotKey]: file }));
    setFixedPreviews(prev => ({ ...prev, [slotKey]: URL.createObjectURL(file) }));
  };

  const clearFixedPhoto = (slotKey) => {
    if (fixedPreviews[slotKey]) URL.revokeObjectURL(fixedPreviews[slotKey]);
    setFixedPhotos(prev  => ({ ...prev, [slotKey]: null }));
    setFixedPreviews(prev => ({ ...prev, [slotKey]: null }));
  };

  const handleAdditionalPhotos = (files) => {
    const arr = Array.from(files);
    const previews = arr.map(f => URL.createObjectURL(f));
    setAdditionalPhotos(prev  => [...prev, ...arr]);
    setAdditionalPreviews(prev => [...prev, ...previews]);
  };

  const removeAdditional = (idx) => {
    URL.revokeObjectURL(additionalPreviews[idx]);
    setAdditionalPhotos(prev  => prev.filter((_, i) => i !== idx));
    setAdditionalPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const fetchVehicleData = async (vehicleNumber) => {
    const clean = vehicleNumber.replace(/[^A-Z0-9]/g, "");
    if (clean.length < 8) return;
 
    setVehicleLookupLoading(true);
    setVehicleError(null);
    setVehicleRcData(null);
 
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/vehicle-details/${vehicleNumber}`
      );
  
      if (!response.ok) {
        setVehicleError(response.status === 404 ? "not_found" : "lookup_failed");
        return;
      }
  
      const vd = await response.json();
      setVehicleRcData(vd); // ← store the full ULIP payload
  
    } catch {
      setVehicleError("lookup_failed");
    } finally {
      setVehicleLookupLoading(false);
    }
  }

  useEffect(() => {
    if (form.vehicle_number) fetchVehicleData(form.vehicle_number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVehicleNumberChange = (e) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const updated = { ...form, vehicle_number: val };
    setForm(updated);
    if (submitted) setErrors(validateForm(updated));
  
    if (vehicleError) setVehicleError(null);
    if (vehicleRcData) setVehicleRcData(null);
    if (val.length >= 8) fetchVehicleData(val);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (opts) => {
    setSubmitted(true);
    const errs = validateForm(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;   // block submission
    }

    const fd = new FormData();
    fd.append("service_type",      serviceType);
    fd.append("created_by_name",    userName);
    fd.append("vehicle_number",    form.vehicle_number.trim().toUpperCase());
    fd.append("pickup_location",   form.pickup_location.trim());
    fd.append("drop_location",     form.drop_location.trim());

    // Link this request to the accident case (backend validates it exists and
    //        rejects duplicates unless allow_duplicate is sent).

    if (accidentCaseNo) fd.append("accident_case_no", accidentCaseNo);
    if (opts?.allowDuplicate) fd.append("allow_duplicate", "1");

    // ★ Append coordinates if available (WhatsApp bot + map picker)
    if (pickupCoords) {
      fd.append("pickup_lat", pickupCoords.lat);
      fd.append("pickup_lng", pickupCoords.lng);
    }
    if (dropCoords) {
      fd.append("drop_lat", dropCoords.lat);
      fd.append("drop_lng", dropCoords.lng);
    }
    
    const source = pickupCoords && dropCoords
                  ? (pickupCoords.fromMap || dropCoords.fromMap ? "map_pin" : "autocomplete")
                  : "typed";
  fd.append("location_source", source);
   // ★ Append ULIP RC data if lookup succeeded
    if (vehicleRcData) {
      fd.append("vehicle_rc_data", JSON.stringify(vehicleRcData));
    }

    if (form.issue_description.trim()) fd.append("issue_description", form.issue_description.trim());
    if (form.owner_name.trim())        fd.append("owner_name",        form.owner_name.trim());
    if (form.owner_contact.trim())     fd.append("owner_contact",     form.owner_contact.trim());
    if (form.driver_name.trim())       fd.append("driver_name",       form.driver_name.trim());
    if (form.driver_contact.trim())    fd.append("driver_contact",    form.driver_contact.trim());
    fd.append("urgent", form.urgent ? "1" : "0");

    FIXED_SLOTS.forEach(({ key }) => {
      if (fixedPhotos[key]) fd.append(key, fixedPhotos[key]);
    });
    additionalPhotos.forEach(file => fd.append("additional", file));

    try {
      await mutation.mutateAsync(fd);
      onCreated?.();
      onClose();
    } catch (err) {
      console.error("Create quick service failed:", err);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
    <div className="qs-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="qs-modal">

        {/* Header */}
        <div className="qs-modal-header">
          <div>
            <h2 className="qs-modal-title">
              New {SERVICE_LABEL[serviceType] || serviceType} request
            </h2>
            <p className="qs-modal-sub">
              {accidentCaseNo
                ? <>Linked to case <strong style={{ fontFamily: "var(--wd-mono)" }}>{accidentCaseNo}</strong></>
                : "Create a request on behalf of the customer"}
            </p>          
          </div>
          <button className="qs-modal-close" onClick={onClose} aria-label="Close">
            <XIcon />
          </button>
        </div>

        {/* Body */}
        <div className="qs-modal-body">

          {/* ── Vehicle & location ── */}
          <div className="qs-modal-section-title">Vehicle &amp; location</div>
          <div className="cr-form-grid">
            <Field label="Vehicle plate no. *" error={errors.vehicle_number}>
                <div style={{ position: "relative" }}>
              <input
                className={`cr-input cr-input--plate ${errors.vehicle_number ? "cr-input--error" : ""}`}
                value={form.vehicle_number}
                onChange={handleVehicleNumberChange}
                placeholder="HR46E8144"
              />
                {/* Spinner while fetching */}
                {vehicleLookupLoading && (
                  <span className="cr-plate-badge cr-plate-badge--loading">
                    <span className="qs-btn-spinner" style={{ width: 9, height: 9, borderWidth: 1.5 }} />
                    Fetching…
                  </span>
                )}
                {/* Green "RC fetched" badge */}
                {!vehicleLookupLoading && vehicleRcData && (
                  <span className="cr-plate-badge cr-plate-badge--ok">
                    <CheckSmIcon /> RC found
                  </span>
                )}
            </div>
            {/* Inline RC error banners — compact, sits right under the field */}
              {!vehicleLookupLoading && vehicleError === "not_found" && (
                <span className="cr-rc-notice cr-rc-notice--warn">
                  <WarnSmIcon /> No RC record found — details can be added manually later.
                </span>
              )}
              {!vehicleLookupLoading && vehicleError === "lookup_failed" && (
                <span className="cr-rc-notice cr-rc-notice--warn">
                  <WarnSmIcon /> RC lookup unavailable.{" "}
                  <button
                    type="button"
                    className="cr-rc-retry"
                    onClick={() => fetchVehicleData(form.vehicle_number)}
                  >
                    Retry
                  </button>
                </span>
              )}
            </Field>

             {/* ★ Pickup location — with autocomplete + map button */}
              <Field label="Pickup location *" full error={errors.pickup_location}>
                <div className="cr-location-wrap">
                  <div className="cr-location-input-row">
                    <input
                      className={`cr-input cr-location-input ${errors.pickup_location ? "cr-input--error" : ""}`}
                      value={form.pickup_location}
                      onChange={handleLocationInput("pickup_location")}
                      onBlur={handleLocationBlur("pickup_location")}
                      onFocus={() => pickupSugg.length > 0 && setShowPickupSugg(true)}
                      placeholder="Search or pin on map"
                      autoComplete="off"
                    />
                    {/* Coordinates badge */}
                    {pickupCoords && (
                      <span className="cr-coords-badge" title={`${pickupCoords.lat.toFixed(5)}, ${pickupCoords.lng.toFixed(5)}`}>
                        <PinIcon /> Pinned
                      </span>
                    )}
                    <button
                      type="button"
                      className="cr-map-btn"
                      onClick={() => setMapPickerField("pickup")}
                      title="Pick on map"
                    >
                      <MapIcon />
                    </button>
                  </div>

                  {/* Pickup suggestions */}
                  {showPickupSugg && (
                    <ul className="cr-suggestions">
                      {pickupSugg.map((s, i) => (
                        <li
                          key={s.place_id ?? i}
                          className="cr-suggestion-item"
                          onMouseDown={() => handleSuggestionSelect("pickup_location", s)}
                        >
                          <span className="cr-sugg-icon"><PinIcon /></span>
                          <div className="cr-sugg-text">
                            <span className="cr-sugg-main">
                              {s.structured_formatting?.main_text ?? s.description}
                            </span>
                            {s.structured_formatting?.secondary_text && (
                              <span className="cr-sugg-sub">{s.structured_formatting.secondary_text}</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {/* ★ nudge when the location has text but no pin (always the case for a
                           prefilled pickup). Route distance and live tracking need coordinates. */}
                {!pickupCoords && form.pickup_location.trim() && (
                  <span className="cr-rc-notice cr-rc-notice--warn">
                    <WarnSmIcon /> Not pinned. Use the map button so route distance and live tracking work.
                  </span>
                )}
              </Field>

              {/* ★ Drop location — with autocomplete + map button */}
              <Field label="Drop / destination *" full error={errors.drop_location}>
                <div className="cr-location-wrap">
                  <div className="cr-location-input-row">
                    <input
                      className={`cr-input cr-location-input ${errors.drop_location ? "cr-input--error" : ""}`}
                      value={form.drop_location}
                      onChange={handleLocationInput("drop_location")}
                      onBlur={handleLocationBlur("drop_location")}
                      onFocus={() => dropSugg.length > 0 && setShowDropSugg(true)}
                      placeholder="Search or pin on map"
                      autoComplete="off"
                    />
                    {dropCoords && (
                      <span className="cr-coords-badge cr-coords-badge--drop" title={`${dropCoords.lat.toFixed(5)}, ${dropCoords.lng.toFixed(5)}`}>
                        <PinIcon /> Pinned
                      </span>
                    )}
                    <button
                      type="button"
                      className="cr-map-btn cr-map-btn--drop"
                      onClick={() => setMapPickerField("drop")}
                      title="Pick on map"
                    >
                      <MapIcon />
                    </button>
                  </div>

                  {/* Drop suggestions */}
                  {showDropSugg && (
                    <ul className="cr-suggestions">
                      {dropSugg.map((s, i) => (
                        <li
                          key={s.place_id ?? i}
                          className="cr-suggestion-item"
                          onMouseDown={() => handleSuggestionSelect("drop_location", s)}
                        >
                          <span className="cr-sugg-icon"><PinIcon /></span>
                          <div className="cr-sugg-text">
                            <span className="cr-sugg-main">
                              {s.structured_formatting?.main_text ?? s.description}
                            </span>
                            {s.structured_formatting?.secondary_text && (
                              <span className="cr-sugg-sub">{s.structured_formatting.secondary_text}</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {!dropCoords && form.drop_location.trim() && (
                  <span className="cr-rc-notice cr-rc-notice--warn">
                    <WarnSmIcon /> Not pinned. Use the map button so route distance works.
                  </span>
                )}
              </Field>

            <Field label="Issue / vehicle condition" full>
              <textarea
                className="cr-input cr-textarea"
                value={form.issue_description}
                onChange={set("issue_description")}
                placeholder="Describe the damage or issue (optional)"
                rows={3}
              />
            </Field>
          </div>

          {/* ── Customer & driver ── */}
          <div className="qs-modal-section-title" style={{ marginTop: 20 }}>
            Customer &amp; driver
          </div>
          <div className="cr-form-grid">
            <Field label="Customer name *" error={errors.owner_name}>
              <input
                className="cr-input"
                value={form.owner_name}
                onChange={set("owner_name")}
                placeholder="e.g. Rajesh Sharma"
              />
            </Field>
            <Field label="Customer contact *" error={errors.owner_contact}>
              <input
                className={`cr-input ${errors.owner_contact ? "cr-input--error" : ""}`}
                type="tel"
                inputMode="numeric"
                maxLength={10}
                value={form.owner_contact}
                onChange={set("owner_contact")}
                placeholder="9876543210"
              />
            </Field>
            <Field label="Driver / representative name">
              <input
                className="cr-input"
                value={form.driver_name}
                onChange={set("driver_name")}
                placeholder="e.g. Suresh Kumar"
              />
            </Field>
            <Field label="Driver contact" error={errors.driver_contact}>
              <input
                className={`cr-input ${errors.driver_contact ? "cr-input--error" : ""}`}
                type="tel"
                inputMode="numeric"
                maxLength={10}
                value={form.driver_contact}
                onChange={set("driver_contact")}
                placeholder="9876543210"
              />
            </Field>
          </div>

          {/* ── Accident photos ── */}
          <div className="qs-modal-section-title" style={{ marginTop: 20 }}>
            Accident photos <span className="qs-modal-optional">(optional)</span>
          </div>

          <div className="cr-photo-upload-grid">
            {FIXED_SLOTS.map(({ key, label }) => (
              <PhotoSlot
                key={key}
                label={label}
                preview={fixedPreviews[key]}
                onFile={file => handleFixedPhoto(key, file)}
                onClear={() => clearFixedPhoto(key)}
              />
            ))}
          </div>

          <div className="cr-additional-photo-section">
            <div className="cr-additional-label">
              Additional photos
              <span className="qs-modal-optional" style={{ marginLeft: 6 }}>(any number)</span>
            </div>

            {additionalPreviews.length > 0 && (
              <div className="cr-additional-preview-row">
                {additionalPreviews.map((url, idx) => (
                  <div key={idx} className="cr-additional-thumb-wrap">
                    <img src={url} alt={`Extra ${idx + 1}`} className="cr-photo-thumb" />
                    <button
                      className="cr-photo-remove-btn"
                      onClick={() => removeAdditional(idx)}
                      aria-label="Remove photo"
                    >
                      <XSmIcon />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              className="cr-add-photo-btn"
              onClick={() => additionalInputRef.current?.click()}
            >
              <PlusIcon /> Add photos
            </button>
            <input
              ref={additionalInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={e => {
                if (e.target.files?.length) handleAdditionalPhotos(e.target.files);
                e.target.value = "";
              }}
            />
          </div>

          {/* ── Urgent flag ── */}
          <label className="cr-checkbox-row" style={{ marginTop: 16 }}>
            <input
              type="checkbox"
              className="cr-checkbox"
              checked={form.urgent}
              onChange={set("urgent")}
            />
            <span className="cr-checkbox-label">Mark as urgent</span>
            <span className="cr-checkbox-sub"> — will appear highlighted in the list</span>
          </label>

          {mutation.isError && (
            <p className="cr-error" style={{ marginTop: 12 }}>
              {mutation.error?.response?.data?.message || "Failed to create request. Please try again."}
            </p>
          )}
           {/* ★ lets the admin override the duplicate check on purpose
                     (e.g. two cranes for a truck: prime mover + trailer). */}
          {mutation.error?.response?.data?.code === "DUPLICATE_LINKED_SERVICE" && (
            <button
              type="button"
              className="wd-btn wd-btn-ghost wd-btn-sm"
              style={{ marginTop: 8 }}
              onClick={() => handleSubmit({ allowDuplicate: true })}
              disabled={mutation.isPending}
            >
              Create anyway
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="qs-modal-footer">
          <button className="wd-btn wd-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="wd-btn wd-btn-primary"
            onClick={() => handleSubmit()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <><span className="qs-btn-spinner" /> Creating…</>
            ) : (
              <><PlusIcon /> Create request</>
            )}
          </button>
        </div>
      </div>
      <style>{MODAL_CSS}</style>
    </div>

    {mapPickerField && (
        <LocationPickerModal
          field={mapPickerField}
          initialAddress={
            mapPickerField === "pickup" ? form.pickup_location : form.drop_location
          }
          onConfirm={(result) =>
            handleMapConfirm(
              mapPickerField === "pickup" ? "pickup_location" : "drop_location",
              result
            )
          }
          onClose={() => setMapPickerField(null)}
        />
      )}
    </>
  );
}


// ─── Photo slot component ─────────────────────────────────────────────────────

function PhotoSlot({ label, preview, onFile, onClear }) {
  const inputRef = useRef(null);
  return (
    <div className="cr-photo-slot">
      {preview ? (
        <div className="cr-photo-slot-filled">
          <img src={preview} alt={label} className="cr-photo-slot-img" />
          <button className="cr-photo-remove-btn" onClick={onClear} aria-label={`Remove ${label}`}>
            <XSmIcon />
          </button>
        </div>
      ) : (
        <button className="cr-photo-slot-empty" onClick={() => inputRef.current?.click()}>
          <CameraIcon />
          <span className="cr-photo-slot-label">{label}</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={e => {
          if (e.target.files?.[0]) onFile(e.target.files[0]);
          e.target.value = "";
        }}
      />
      <div className="cr-photo-slot-angle">{label}</div>
    </div>
  );
}

// ─── Field — now accepts an optional `error` prop ─────────────────────────────
function Field({ label, full, error, children }) {
  return (
    <div className={`cr-form-field ${full ? "cr-form-field--full" : ""}`}>
      <label className="cr-form-label">{label}</label>
      {children}
      {error && <span className="cr-field-error"><ErrorIcon />{error}</span>}
    </div>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────
function PlusIcon()  { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function XIcon()     { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function XSmIcon()   { return <svg width="9"  height="9"  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"   strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function CameraIcon(){ return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>; }
function ErrorIcon() { return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,marginTop:1}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function MapIcon()   { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>; }
function PinIcon()   { return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>; }
function CheckSmIcon() { return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>; }
function WarnSmIcon()  { return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{flexShrink:0}}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>; }

// ─── CSS (only additions / changes highlighted with comments) ─────────────────
const MODAL_CSS = `
.qs-modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.72);
  backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
  z-index: 1000; display: flex; align-items: center; justify-content: center;
  padding: 16px; animation: qs-fade-in 0.15s ease;
}
@keyframes qs-fade-in { from { opacity: 0; } to { opacity: 1; } }

.qs-modal {
  background: #161e2e; border: 1px solid #243044; border-radius: 12px;
  width: 100%; max-width: 600px; max-height: 90vh;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04);
  overflow: hidden; animation: qs-slide-up 0.18s ease;
}
@keyframes qs-slide-up {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

.qs-modal-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 18px 24px 16px; border-bottom: 1px solid #1e2c42;
  flex-shrink: 0; background: #111827;
}
.qs-modal-title  { font-size: 15px; font-weight: 600; color: #f1f5f9; margin: 0; }
.qs-modal-sub    { font-size: 12px; color: #64748b; margin: 3px 0 0; }
.qs-modal-optional { font-size: 11px; color: #475569; font-weight: 400; text-transform: none; letter-spacing: 0; }

.qs-modal-close {
  width: 30px; height: 30px; border: none; background: transparent; cursor: pointer;
  border-radius: 6px; color: #64748b;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s, color 0.15s; flex-shrink: 0;
}
.qs-modal-close:hover { background: #1e2c42; color: #cbd5e1; }

.qs-modal-body {
  padding: 20px 24px; overflow-y: auto; flex: 1;
  scrollbar-width: thin; scrollbar-color: #243044 transparent;
}
.qs-modal-body::-webkit-scrollbar       { width: 5px; }
.qs-modal-body::-webkit-scrollbar-thumb { background: #243044; border-radius: 3px; }

.qs-modal-section-title {
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase;
  color: #475569; margin: 0 0 12px; padding-bottom: 7px; border-bottom: 1px solid #1e2c42;
}
.qs-modal-footer {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 14px 24px; border-top: 1px solid #1e2c42;
  flex-shrink: 0; background: #111827;
}

/* ── Form grid ── */
.cr-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 4px; }
.cr-form-field { display: flex; flex-direction: column; gap: 5px; }
.cr-form-field--full { grid-column: span 2; }
.cr-form-label { font-size: 11.5px; font-weight: 500; color: #94a3b8; }

/* ── Field-level error message ── NEW ── */
.cr-field-error {
  display: flex; align-items: flex-start; gap: 4px;
  font-size: 11px; color: #f87171; margin-top: 2px; line-height: 1.4;
  animation: cr-err-in 0.15s ease;
}
@keyframes cr-err-in { from { opacity: 0; transform: translateY(-3px); } to { opacity: 1; transform: translateY(0); } }

/* ── Inputs ── */
.cr-input {
  height: 36px; padding: 0 11px;
  border: 1px solid #243044; border-radius: 7px;
  font-size: 13px; color: #e2e8f0; background: #0f1623;
  outline: none; width: 100%; box-sizing: border-box;
  font-family: inherit; transition: border-color 0.15s, box-shadow 0.15s;
}
.cr-input::placeholder { color: #374f6b; }
.cr-input:focus { border-color: #3b82f6; background: #0d1520; box-shadow: 0 0 0 3px rgba(59,130,246,0.12); }
.cr-input:hover:not(:focus) { border-color: #2d3f58; }
.cr-input--plate { font-family: 'Courier New', Courier, monospace; letter-spacing: 0.06em; text-transform: uppercase; }
.cr-textarea { height: auto; padding: 9px 11px; resize: vertical; min-height: 72px; line-height: 1.5; }

/* ── Error state for inputs ── NEW ── */
.cr-input--error {
  border-color: #ef4444 !important;
  background: rgba(239,68,68,0.05) !important;
  box-shadow: 0 0 0 3px rgba(239,68,68,0.10) !important;
}
.cr-input--error:focus {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3px rgba(239,68,68,0.18) !important;
}
/* ★ Plate RC status badge — sits inside the input on the right */
.cr-plate-badge {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10px; font-weight: 600; border-radius: 20px;
  padding: 2px 7px; pointer-events: none; white-space: nowrap;
}
.cr-plate-badge--loading {
  color: #94a3b8; background: rgba(148,163,184,0.1); border: 1px solid rgba(148,163,184,0.2);
}
.cr-plate-badge--ok {
  color: #22c55e; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.25);
}

/* ★ RC notice line — compact helper text under the plate field */
.cr-rc-notice {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; line-height: 1.4; margin-top: 2px;
}
.cr-rc-notice--warn { color: #f59e0b; }
.cr-rc-retry {
  background: none; border: none; padding: 0;
  font-size: 11px; font-weight: 600; color: #60a5fa;
  cursor: pointer; text-decoration: underline;
}
.cr-rc-retry:hover { color: #93c5fd; }
/* ★ Location input row */
.cr-location-wrap    { position:relative; display:flex; flex-direction:column; width: 100%; }
.cr-location-input-row { display:flex; align-items:center; gap:6px;width: 100%; }
.cr-location-input   { flex:1; min-width: 0; padding-right:11px; }

/* ★ Map button */
.cr-map-btn {
  flex-shrink:0; width:36px; height:36px;
  border:1px solid #243044; border-radius:7px;
  background:#0f1623; color:#64748b;
  display:flex; align-items:center; justify-content:center;
  cursor:pointer; transition:border-color 0.15s,color 0.15s,background 0.15s;
}
.cr-map-btn:hover { border-color:#3b82f6; color:#60a5fa; background:rgba(59,130,246,0.08); }
.cr-map-btn--drop:hover { border-color:#10b981; color:#34d399; background:rgba(16,185,129,0.08); }

/* ★ "Pinned" badge */
.cr-coords-badge {
  display:inline-flex; align-items:center; gap:3px;
  font-size:10px; font-weight:500;
  color:#3b82f6; background:rgba(59,130,246,0.12);
  border:1px solid rgba(59,130,246,0.25);
  border-radius:20px; padding:2px 7px;
  white-space:nowrap; flex-shrink:0;
}
.cr-coords-badge--drop { color:#10b981; background:rgba(16,185,129,0.1); border-color:rgba(16,185,129,0.25); }

/* ★ Suggestions dropdown */
.cr-suggestions {
  position:absolute; top:calc(100% + 4px); left:0; right:0;
  background:#0f1e35; border:1px solid #243044; border-radius:9px;
  list-style:none; margin:0; padding:4px 0;
  z-index: 200;
  max-height:200px; overflow-y:auto;
  box-shadow:0 12px 40px rgba(0,0,0,0.5);
  scrollbar-width:thin; scrollbar-color:#243044 transparent;
}
.cr-suggestion-item {
  display:flex; align-items:flex-start; gap:10px;
  padding:8px 12px; cursor:pointer; transition:background 0.12s;
}
.cr-suggestion-item:hover { background:#162032; }
.cr-sugg-icon  { color:#475569; flex-shrink:0; margin-top:2px; }
.cr-sugg-text  { display:flex; flex-direction:column; gap:2px; min-width:0; }
.cr-sugg-main  { font-size:12.5px; color:#e2e8f0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.cr-sugg-sub   { font-size:11px; color:#64748b; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

/* ── Photo upload grid ── */
.cr-photo-upload-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px; }

.cr-photo-slot { display: flex; flex-direction: column; align-items: center; gap: 5px; }

.cr-photo-slot-empty {
  width: 100%; aspect-ratio: 4/3; border: 1.5px dashed #243044; border-radius: 8px;
  background: #0f1623; display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 6px; cursor: pointer; color: #374f6b;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}
.cr-photo-slot-empty:hover { border-color: #3b82f6; color: #60a5fa; background: rgba(59,130,246,0.05); }
.cr-photo-slot-label { font-size: 10px; color: inherit; }

.cr-photo-slot-filled {
  width: 100%; aspect-ratio: 4/3; border-radius: 8px; overflow: hidden;
  position: relative; border: 1px solid #243044;
}
.cr-photo-slot-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.cr-photo-slot-angle { font-size: 10px; color: #475569; text-align: center; }

.cr-photo-remove-btn {
  position: absolute; top: 4px; right: 4px; width: 18px; height: 18px;
  border-radius: 50%; background: rgba(0,0,0,0.7); border: 1px solid rgba(255,255,255,0.15);
  color: #f1f5f9; display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background 0.15s;
}
.cr-photo-remove-btn:hover { background: rgba(239,68,68,0.8); }

.cr-additional-photo-section { display: flex; flex-direction: column; gap: 8px; margin-bottom: 4px; }
.cr-additional-label { font-size: 11.5px; font-weight: 500; color: #94a3b8; }
.cr-additional-preview-row { display: flex; gap: 8px; flex-wrap: wrap; }
.cr-additional-thumb-wrap {
  position: relative; width: 64px; height: 52px; border-radius: 6px;
  overflow: hidden; border: 1px solid #243044; flex-shrink: 0;
}
.cr-photo-thumb { width: 100%; height: 100%; object-fit: cover; display: block; }

.cr-add-photo-btn {
  display: inline-flex; align-items: center; gap: 5px; font-size: 12px;
  padding: 6px 13px; border-radius: 7px; border: 1px dashed #2d3f58;
  background: transparent; color: #64748b; cursor: pointer; align-self: flex-start;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}
.cr-add-photo-btn:hover { border-color: #3b82f6; color: #60a5fa; background: rgba(59,130,246,0.05); }

.cr-checkbox-row { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.cr-checkbox     { width: 15px; height: 15px; accent-color: #3b82f6; cursor: pointer; flex-shrink: 0; }
.cr-checkbox-label { font-size: 13px; font-weight: 500; color: #cbd5e1; }
.cr-checkbox-sub   { font-size: 12px; color: #475569; }

.cr-error { font-size: 12px; color: #f87171; margin: 0; }

.qs-btn-spinner {
  width: 12px; height: 12px; border: 2px solid rgba(255,255,255,0.25);
  border-top-color: #fff; border-radius: 50%;
  animation: qs-spin 0.65s linear infinite; display: inline-block;
}
@keyframes qs-spin { to { transform: rotate(360deg); } }

@media (max-width: 600px) {
  .cr-form-grid          { grid-template-columns: 1fr; }
  .cr-form-field--full   { grid-column: span 1; }
  .cr-photo-upload-grid  { grid-template-columns: repeat(2, 1fr); }
}
`;